<template>
  <section class="lending-schedule-panel">
    <b-card>
      <b-row
        v-if="showStoreFilter"
        class="mb-1"
      >
        <b-col md="4">
          <e-store-combo
            id="lending-schedule-panel-store"
            :value="storeId"
            :required="false"
            :clearable="true"
            :select-single-option="false"
            :placeholder="$t('Todas as lojas')"
            @input="onStoreChanged"
          />
        </b-col>
      </b-row>

      <b-overlay
        :show="loading"
        rounded="sm"
        opacity="0.4"
      >
        <full-calendar
          ref="calendar"
          :options="calendarOptions"
        />
      </b-overlay>
    </b-card>

    <b-modal
      id="modal-lending-schedule-detail"
      v-model="showDetail"
      :title="detailTitle"
      centered
      size="md"
    >
      <template v-if="selected">
        <dl class="row mb-0">
          <dt class="col-sm-4">
            {{ $t('Horário') }}
          </dt>
          <dd class="col-sm-8">
            {{ formatPeriod(selected) }}
          </dd>

          <dt class="col-sm-4">
            {{ $t('Cliente') }}
          </dt>
          <dd class="col-sm-8">
            {{ selected.customerName || '-' }}
          </dd>

          <dt class="col-sm-4">
            {{ $t('Loja') }}
          </dt>
          <dd class="col-sm-8">
            {{ selected.storeName || '-' }}
          </dd>

          <dt class="col-sm-4">
            {{ $t('Status do pedido') }}
          </dt>
          <dd class="col-sm-8">
            {{ saleStatusLabel[selected.status] || selected.status }}
          </dd>

          <dt class="col-sm-4">
            {{ $t('Status da entrega') }}
          </dt>
          <dd class="col-sm-8">
            <b-badge :variant="deliveryStatusVariant(selected.deliveryStatus)">
              {{ deliveryStatusLabel[selected.deliveryStatus] || '-' }}
            </b-badge>
          </dd>

          <dt class="col-sm-4">
            {{ $t('Endereço') }}
          </dt>
          <dd class="col-sm-8">
            {{ formatAddress(selected.deliveryAddress) }}
          </dd>

          <template v-if="selected.deliveryObservation">
            <dt class="col-sm-4">
              {{ $t('Observação') }}
            </dt>
            <dd class="col-sm-8">
              {{ selected.deliveryObservation }}
            </dd>
          </template>

          <dt class="col-sm-4">
            {{ $t('Itens de comodato') }}
          </dt>
          <dd class="col-sm-8">
            <ul
              v-if="selected.items.length"
              class="pl-1 mb-0"
            >
              <li
                v-for="(item, index) in selected.items"
                :key="index"
              >
                {{ item.quantity }} x {{ item.name || '-' }}
              </li>
            </ul>
            <span v-else>-</span>
          </dd>
        </dl>
      </template>

      <template #modal-footer>
        <e-button
          id="modal-lending-schedule-detail-btn_close"
          class="mr-1"
          variant="outline-primary"
          :text="$t('Fechar')"
          @click="showDetail = false"
        />
        <e-button
          id="modal-lending-schedule-detail-btn_open_order"
          variant="primary"
          :text="$t('Abrir pedido')"
          @click="openOrder"
        />
      </template>
    </b-modal>
  </section>
</template>

<script>
import { BCard, BRow, BCol, BOverlay, BModal, BBadge } from 'bootstrap-vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import moment from 'moment'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import ptBrLocale from '@fullcalendar/core/locales/pt-br'
import EButton from '@/views/components/EButton.vue'
import EStoreCombo from '@/views/components/inputs/EStoreCombo.vue'
import { statusTypes } from '@/mixins'

// Cor do agendamento pelo status da entrega (o pedido cancelado não aparece no painel).
const deliveryStatusVariants = {
  Pending: 'primary',
  Preparing: 'warning',
  InProgress: 'info',
  Completed: 'success',
}

/**
 * Painel de agendamento de entrega de chope/comodato: os agendamentos ativos das lojas do usuário
 * em visões Mês (padrão), Semana e Dia. O filtro de loja só aparece para quem acessa mais de uma
 * loja; sem loja escolhida, o painel mostra todas.
 */
export default {
  components: {
    BCard,
    BRow,
    BCol,
    BOverlay,
    BModal,
    BBadge,
    EButton,
    EStoreCombo,
    FullCalendar,
  },

  mixins: [statusTypes],

  data() {
    return {
      loading: false,
      showDetail: false,
      selected: null,
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin],
        locale: ptBrLocale,
        initialView: 'dayGridMonth',
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        },
        height: 'auto',
        dayMaxEvents: true,
        nowIndicator: true,
        allDaySlot: false,
        scrollTime: '08:00:00',
        eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
        slotLabelFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
        events: this.loadEvents,
        eventClick: this.onEventClick,
        loading: this.onLoading,
      },
    }
  },

  computed: {
    ...mapGetters('app', ['storeOptions']),
    ...mapState('pages/sale/lendingSchedulePanel', ['storeId']),

    // Com acesso a uma única loja não há o que filtrar: o combo nem aparece.
    showStoreFilter() {
      return this.storeOptions.length > 1
    },

    // Lojas enviadas à API: a única loja do usuário, a escolhida no filtro, ou todas (vazio).
    storeIds() {
      if (this.storeOptions.length === 1) return [this.storeOptions[0].id]
      return this.storeId ? [Number(this.storeId)] : []
    },

    // O nome da loja só ajuda a distinguir os agendamentos quando o painel mostra várias lojas.
    showStoreInTitle() {
      return this.showStoreFilter && !this.storeId
    },

    detailTitle() {
      return this.selected ? `${this.$t('Pedido')} #${this.selected.saleId}` : ''
    },
  },

  watch: {
    // As lojas do usuário chegam depois do carregamento da aplicação: recarrega o painel para
    // aplicar a regra de loja única e o nome da loja nos agendamentos.
    storeOptions() {
      this.refetch()
    },
  },

  methods: {
    ...mapActions('pages/sale/lendingSchedulePanel', ['fetchSchedules', 'setStoreId']),

    async loadEvents(fetchInfo, successCallback, failureCallback) {
      try {
        const schedules = await this.fetchSchedules({
          from: fetchInfo.start,
          to: fetchInfo.end,
          storeIds: this.storeIds,
        })
        successCallback(schedules.map(this.toEvent))
      } catch (error) {
        this.showGenericError({ error })
        failureCallback(error)
      }
    },

    toEvent(schedule) {
      const customer = schedule.customerName || this.$t('Cliente não informado')
      const store = this.showStoreInTitle && schedule.storeName ? ` (${schedule.storeName})` : ''
      const variant = deliveryStatusVariants[schedule.deliveryStatus] || 'secondary'

      return {
        id: String(schedule.saleId),
        title: `#${schedule.saleId} - ${customer}${store}`,
        start: schedule.start,
        end: schedule.end,
        classNames: ['lending-schedule-event', `lending-schedule-event--${variant}`],
        extendedProps: { schedule },
      }
    },

    onLoading(isLoading) {
      this.loading = isLoading
    },

    onEventClick({ event, jsEvent }) {
      jsEvent.preventDefault()
      this.selected = event.extendedProps.schedule
      this.showDetail = true
    },

    onStoreChanged(storeId) {
      this.setStoreId(storeId)
      this.refetch()
    },

    refetch() {
      this.$nextTick(() => this.$refs.calendar?.getApi().refetchEvents())
    },

    openOrder() {
      const name = this.$can('Update', 'Sale') ? 'order-maintain' : 'order-read-only'
      this.$router.push({ name, params: { id: this.selected.saleId } })
    },

    deliveryStatusVariant(deliveryStatus) {
      return deliveryStatusVariants[deliveryStatus] || 'secondary'
    },

    // Horário no fuso do pedido (o da loja), como no restante do agendamento.
    formatPeriod(schedule) {
      const start = moment.parseZone(schedule.start)
      const end = moment.parseZone(schedule.end)
      return `${start.format('DD/MM/YYYY HH:mm')} - ${end.format('HH:mm')}`
    },

    formatAddress(address) {
      if (!address) return '-'
      const street = [address.publicPlace, address.number].filter(Boolean).join(', ')
      const city = [address.city, address.province].filter(Boolean).join('/')
      return [street, address.complement, address.neighborhood, city].filter(Boolean).join(' - ') || '-'
    },
  },
}
</script>

<style lang="scss">
.lending-schedule-panel {
  .fc .fc-toolbar-title {
    font-size: 1.25rem;
    text-transform: capitalize;
  }

  .fc .fc-button-primary {
    background-color: var(--primary);
    border-color: var(--primary);
  }

  .fc .fc-button-primary:not(:disabled).fc-button-active,
  .fc .fc-button-primary:not(:disabled):active {
    filter: brightness(0.85);
  }

  .lending-schedule-event {
    cursor: pointer;
  }

  @each $variant in primary, warning, info, success, secondary {
    .lending-schedule-event--#{$variant} {
      background-color: var(--#{$variant});
      border-color: var(--#{$variant});
      color: #fff;

      .fc-event-main,
      .fc-event-time,
      .fc-event-title {
        color: #fff;
      }

      // Na visão Mês, os agendamentos com horário viram uma linha com um ponto colorido.
      &.fc-daygrid-dot-event {
        background-color: transparent;
        color: inherit;

        .fc-daygrid-event-dot {
          border-color: var(--#{$variant});
        }

        .fc-event-time,
        .fc-event-title {
          color: inherit;
        }
      }
    }
  }
}
</style>
